<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :embedded="embedded"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">
    <div class="row no-gutters h-100">
      <div class="col-2 p-2">
        <AnalyticsDashboardValueRow>
          <div class="col">
            <ReportFilterPopover
              headerTitle="Production Stage Task Filter"
              :showApplyButton="true"
              :showResetButton="true"
              :appendToBody="true"
              alignment="left"
              placement="bottom-end"
              @apply="applyStageTaskFilter"
              @reset="resetStageTaskFilter">
              <template v-slot:popover>
                <ReportDateRangeSelect 
                  :startDate="editingStageTaskFilter.startDate"
                  :endDate="editingStageTaskFilter.endDate"
                  @updateStartDate="editingStageTaskFilter.startDate = $event"
                  @updateEndDate="editingStageTaskFilter.endDate = $event"
                />
                <FormHorizontalRule />
                Production Stage
                <FormSelect :options="productionStageLookup" v-model="editingStageTaskFilter.productionStageId" />

              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedStageTaskFilter.startDate | formatDate}} - {{appliedStageTaskFilter.endDate | formatDate}}
                </div>                
              </template>
            </ReportFilterPopover>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Production Stage" highlight>
              {{stageTaskSummaryData.productionStageName}}
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col-6 pr-1">
            <AnalyticsDashboardValue title="Total Panels Completed">
              {{formatInteger(stageTaskSummaryData.totalCompleted, false, true)}}
              <template v-slot:info>During the time period</template>
            </AnalyticsDashboardValue>
          </div>
          <div class="col-6 pl-1">
            <AnalyticsDashboardValue title="Panels Completed Early">
              {{formatInteger(stageTaskSummaryData.totalCompletedEarly, false, true)}}
              <template v-slot:info>During the time period</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col-6 pr-1">
            <AnalyticsDashboardValue title="Panels Completed On Schedule">
              {{formatInteger(stageTaskSummaryData.totalCompletedSameDay, false, true)}}
              <template v-slot:info>During the time period</template>
            </AnalyticsDashboardValue>
          </div>
          <div class="col-6 pl-1">
            <AnalyticsDashboardValue title="Panels Completed Late">
              {{formatInteger(stageTaskSummaryData.totalCompletedOverdue, false, true)}}
              <template v-slot:info>During the time period</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Average Units Per Panel">
              <UOMValue :uomType="stageUoMType" :value="stageTaskSummaryData.averageUnits" :precision="2" />
              <template v-slot:info>Average units <strong>per panel</strong> completed/due during the time period for {{formatInteger(stageTaskSummaryData.totalTasks, false, true)}} items over {{formatInteger(stageTaskSummaryData.totalDays, false, true)}} days</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Total Panel Units Completed">
              <UOMValue :uomType="stageUoMType" :value="stageTaskSummaryData.totalCompletedUnits" :precision="2" />
              <template v-slot:info>Total units completed during the time period for {{formatInteger(stageTaskSummaryData.totalCompletedTasks, false, true)}} items over {{formatInteger(stageTaskSummaryData.totalDays, false, true)}} days</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Total Panel Area Completed" v-if="!isStageUoMTypeArea">
              <UOMValue :uomType="UOMTypes.metresq" :value="stageTaskSummaryData.totalArea" :precision="2" />
              <template v-slot:info>Total area completed during the time period for {{formatInteger(stageTaskSummaryData.totalCompletedTasks, false, true)}} items over {{formatInteger(stageTaskSummaryData.totalDays, false, true)}} days</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
      </div>
      <div class="col-10">
        <ChartBar
          title="Daily Stage Panels"
          :series="stageTaskDailyData.series"
          :xAxis="stageTaskDailyData.xAxis"
          :yAxis="stageTaskDailyData.yAxis"
          :legend="stageTaskDailyData.legend"
          :options="stageTaskDailyData.options"
          :popupEnabled="true"
          :exportEnabled="true"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import Utils from '@/utils/utils'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import ChartBar from '@/components/charts/chartBar'

import { UOMTypes } from '@/store/types'

export default {
  name: 'OrderDailyChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    filter: Object,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  data() {
    return {
      productionStageList: [],
      stageTaskSummaryData: {
        productionStageName: '',
        totalCompleted: 0,
        totalCompletedEarly: 0,
        totalCompletedSameDay: 0,
        totalCompletedOverdue: 0,
        totalUnits: 0,
        totalCompletedUnits: 0,
        totalTasks: 0,
        totalCompletedTasks: 0,
        totalDays: 0,
        totalArea: 0,
        totalCompletedArea: 0,
        averageUnits: 0,
        averageUnitsPerDay: 0,
        totalAreaPerDay: 0
      },
      stageTaskDailyData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultStageTaskFilter: {    
        startDate: moment().local().subtract(90, 'days').startOf('day').toDate().toISOString(),
        endDate: moment().local().add(15, 'days').endOf('day').toDate().toISOString(),
        productionStageId: null
      },
      editingStageTaskFilter: {},
      appliedStageTaskFilter: {},

      productionStageLookup: []
    }
  },
  computed: {
    selectedProductionStage() {
      return this.productionStageList.find(stage => stage.id == this.appliedStageTaskFilter.productionStageId)
    },
    stageUoMType() {
      if(this.selectedProductionStage) {
        // Get UoM type of first active station in the stage
        let activeStation = this.selectedProductionStage.productionStations.find(station => station.isActive)
        if(activeStation)
          return activeStation.uomType
      }
      return null
    },
    isStageUoMTypeArea() {
      return this.stageUoMType == UOMTypes.metresq
    }
  },
  created() {
    this.UOMTypes = UOMTypes
  },
  mounted() {
    this.editingStageTaskFilter = _.clone(this.defaultStageTaskFilter)
    this.appliedStageTaskFilter = _.clone(this.defaultStageTaskFilter)

    // Note: Only load chart data when we have a production stage set
    this.requestData()

    this.subscribeEndpoint('/production/sheetschedule', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/production/sheetschedule')
  },
  watch: {
    filter: function() {
      this.requestChartData()
    }
  },
  methods: {
    applyStageTaskFilter() {
      this.appliedStageTaskFilter = _.clone(this.editingStageTaskFilter)

      this.requestChartData()
    },
    resetStageTaskFilter() {
      this.editingStageTaskFilter = _.clone(this.defaultStageTaskFilter)
      this.appliedStageTaskFilter = _.clone(this.defaultStageTaskFilter)

      this.requestChartData()
    },
    requestData() {
      // Load the production stages
      this.$apiService.productionService.getProductionStageList()
      .then(response => {
        this.productionStageList = _.cloneDeep(response.data)

        this.productionStageLookup = response.data
        .filter(productionStage => productionStage.isActive)
        .map(productionStage => {
          return {
            value: productionStage.id,
            name: productionStage.name
          }
        })

        // Apply the 1st stage to the default filter
        if(this.productionStageLookup.length > 0) {
          this.defaultStageTaskFilter.productionStageId = this.productionStageLookup[0].value
          this.resetStageTaskFilter()
        }
      })
      .catch(err => {
        this.displayNetworkError(err, 'Could not load data')
      })
    },
    requestChartData() {
      var stageTaskFilter = {
        type: 'daily',
        startDate: this.appliedStageTaskFilter.startDate,
        endDate: this.appliedStageTaskFilter.endDate,
        productionStageId: this.appliedStageTaskFilter.productionStageId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null,
      }

      this.stageTaskDailyData.options = {
        aria: {
          enabled: true,
          decal: {
            show: true
          }
        }
      }

      this.$apiService.analyticsService.getProductionStagesChartData(stageTaskFilter)
      .then(response => {
        let chartData = response.data

        this.stageTaskSummaryData.productionStageName = this.formatLookupName(stageTaskFilter.productionStageId, this.productionStageLookup)
        this.stageTaskSummaryData.totalCompletedEarly = chartData.reduce((curValue, item) => curValue + item.totalCompletedEarly, 0)
        this.stageTaskSummaryData.totalCompletedSameDay = chartData.reduce((curValue, item) => curValue + item.totalCompletedSameDay, 0)
        this.stageTaskSummaryData.totalCompletedOverdue = chartData.reduce((curValue, item) => curValue + item.totalCompletedOverdue, 0)
        this.stageTaskSummaryData.totalCompleted = this.stageTaskSummaryData.totalCompletedEarly + this.stageTaskSummaryData.totalCompletedSameDay + this.stageTaskSummaryData.totalCompletedOverdue

        this.stageTaskSummaryData.totalUnits = chartData.reduce((curValue, item) => curValue + item.totalUnits, 0)
        this.stageTaskSummaryData.totalCompletedUnits = chartData.reduce((curValue, item) => curValue + item.totalCompletedUnits, 0)
        this.stageTaskSummaryData.totalTasks = chartData.reduce((curValue, item) => curValue + item.totalTasks, 0)
        this.stageTaskSummaryData.totalCompletedTasks = chartData.reduce((curValue, item) => curValue + item.totalCompletedTasks, 0)
        this.stageTaskSummaryData.totalArea = chartData.reduce((curValue, item) => curValue + item.totalArea, 0)
        this.stageTaskSummaryData.totalCompletedArea = chartData.reduce((curValue, item) => curValue + item.totalCompletedArea, 0)
        this.stageTaskSummaryData.totalDays = chartData.length
        this.stageTaskSummaryData.averageUnits = this.stageTaskSummaryData.totalUnits / (this.stageTaskSummaryData.totalTasks || 1)
        this.stageTaskSummaryData.averageUnitsPerDay = this.stageTaskSummaryData.totalUnits / (this.stageTaskSummaryData.totalDays || 1)
        this.stageTaskSummaryData.averageAreaPerDay = this.stageTaskSummaryData.totalArea / (this.stageTaskSummaryData.totalDays || 1)

        var totalCompletedChartData = chartData.map(item => {
          return {
            dateStr: moment(item.date).format('DD MMM'),
            totalCompleted: (item.totalCompletedEarly + item.totalCompletedSameDay + item.totalCompletedOverdue)
          }
        }).filter(item => item.totalCompleted > 0)

        // Update date string
        chartData.forEach(dataEntry => {
          dataEntry.dateStr = moment(dataEntry.date).format('DD MMM')
        })

        this.stageTaskDailyData.legend = {
        }
        this.stageTaskDailyData.xAxis = {
          exportName: 'Date',
          exportData: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM YYYY')),
          data: chartData.map(chartEntry => chartEntry.dateStr)
        }
        this.stageTaskDailyData.yAxis = [
          {
            type: 'value'
          }
        ]
        this.stageTaskDailyData.series = []
        this.stageTaskDailyData.series.push({
          type: 'bar',
          name: 'Total Completed',
          barWidth: '10%',
          stack: 'total',
          emphasis: {
            focus: 'series'
          },
          data: totalCompletedChartData.map(chartEntry => {
            return [chartEntry.dateStr, chartEntry.totalCompleted]
          })
        })

        this.stageTaskDailyData.series.push({
          type: 'bar',
          name: 'Completed Early',
          stack: 'tasks',
          emphasis: {
            focus: 'series'
          },
          itemStyle: {
            decal: {
              dashArrayX: [1, 0],
              dashArrayY: [2, 5],
              symbolSize: 1,
              rotation: Math.PI / 6
            },
            color: '#91CC75'
          },
          data: chartData.filter(chartEntry => chartEntry.totalCompletedEarly > 0).map(chartEntry => {
            return [chartEntry.dateStr, chartEntry.totalCompletedEarly]
          })
        })
        this.stageTaskDailyData.series.push({
          type: 'bar',
          name: 'Completed On Schedule',
          stack: 'tasks',
          emphasis: {
            focus: 'series'
          },
          itemStyle: {
            decal: {
              dashArrayX: [1, 0],
              dashArrayY: [2, 5],
              symbolSize: 1,
              rotation: Math.PI / 6
            },
            color: '#FAC858'
          },
          data: chartData.filter(chartEntry => chartEntry.totalCompletedSameDay > 0).map(chartEntry => {
            return [chartEntry.dateStr, chartEntry.totalCompletedSameDay]
          })
        })
        this.stageTaskDailyData.series.push({
          type: 'bar',
          name: 'Completed Overdue',
          stack: 'tasks',
          emphasis: {
            focus: 'series'
          },
          itemStyle: {
            decal: {
              dashArrayX: [1, 0],
              dashArrayY: [2, 5],
              symbolSize: 1,
              rotation: Math.PI / 6
            },
            color: '#EE6666'
          },
          data: chartData.filter(chartEntry => chartEntry.totalCompletedOverdue > 0).map(chartEntry => {
            return [chartEntry.dateStr, chartEntry.totalCompletedOverdue]
          })
        })
        this.stageTaskDailyData.series.push({
          type: 'bar',
          name: 'Pending',
          stack: 'tasks',
          emphasis: {
            focus: 'series'
          },
          itemStyle: {
            color: '#91CC75'
          },
          data: chartData.filter(chartEntry => chartEntry.totalPendingFuture > 0).map(chartEntry => {
            return [chartEntry.dateStr, chartEntry.totalPendingFuture]
          })
        })
        this.stageTaskDailyData.series.push({
          type: 'bar',
          name: 'Due Today',
          stack: 'tasks',
          emphasis: {
            focus: 'series'
          },
          itemStyle: {
            color: '#FAC858'
          },
          data: chartData.filter(chartEntry => chartEntry.totalPendingSameDay > 0).map(chartEntry => {
            return [chartEntry.dateStr, chartEntry.totalPendingSameDay]
          })
        })
        this.stageTaskDailyData.series.push({
          type: 'bar',
          name: 'Overdue',
          stack: 'tasks',
          emphasis: {
            focus: 'series'
          },
          itemStyle: {
            color: '#EE6666'
          },
          data: chartData.filter(chartEntry => chartEntry.totalPendingOverdue > 0).map(chartEntry => {
            return [chartEntry.dateStr, chartEntry.totalPendingOverdue]
          })
        })
      })
    },
  }
}
</script>